// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import styles from '../Backstage.module.css'
import type { ChatRow } from '../utils/types'
import { ToolNote } from './ToolNote'
import { isToolNote } from '../utils/toolNames'

interface ChatBubbleProps {
    message: ChatRow
    onMessageClick: (nodeIds: string[]) => void
    showToolLoader?: boolean
    count?: number
}

export function ChatBubble({
    message: m,
    onMessageClick,
    showToolLoader,
    count = 1
}: ChatBubbleProps): ReactElement {
    const { t } = useTranslation()
    const hasNodes = m.kind === 'bubble' && m.nodeIds && m.nodeIds.length > 0
    const isClickable = hasNodes

    if (m.kind === 'note') {
        if (isToolNote(m.text)) {
            return <ToolNote text={m.text} count={count} active={Boolean(showToolLoader)} />
        }
        return (
            <div className={styles.msgRow}>
                <div className={styles.msgNote}>{m.text}</div>
            </div>
        )
    }

    if (m.kind === 'error') {
        const isCreditsError = m.errorType === 'insufficient_credits'
        const isToolError = m.errorType === 'tool_error'
        if (isToolError) {
            return (
                <div className={styles.msgRow}>
                    <div className={styles.toolErrorCard}>
                        <div className={styles.toolErrorHeader}>
                            <AlertTriangle size={14} aria-hidden="true" />
                            <span>{m.text} {t('errors:toolFailed')}</span>
                        </div>
                        <button
                            className={styles.toolErrorReport}
                            onClick={() => {
                                window.dispatchEvent(new CustomEvent('report:error', {
                                    detail: {
                                        toolName: m.toolName,
                                        displayName: m.text,
                                    },
                                }))
                            }}
                        >
                            {t('errors:reportIssue')}
                        </button>
                    </div>
                </div>
            )
        }
        return (
            <div className={styles.msgRow}>
                <div className={styles.msgNote} style={{ color: 'var(--color-danger)' }}>
                    {isCreditsError ? (
                        <>
                            {t('errors:insufficientCreditsAction')}{' '}
                            <button
                                className={styles.inlineLink}
                                onClick={() => window.dispatchEvent(new CustomEvent('open:recharge'))}
                            >
                                {t('billing:rechargeCredits')}
                            </button>
                        </>
                    ) : (
                        <>Error: {m.text}</>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className={styles.msgRow}>
            <div
                className={m.role === 'user' ? styles.msgUser : styles.msgAssistant}
                onClick={isClickable ? () => onMessageClick(m.nodeIds!) : undefined}
                style={{
                    cursor: isClickable ? 'pointer' : 'default',
                    opacity: m.pending ? 0.7 : 1
                }}
                title={hasNodes ? `Click to highlight ${m.nodeIds!.length} node(s)` : undefined}
            >
                {m.text}
            </div>
        </div>
    )
}

